import { saveAs } from 'file-saver';
import { fmtDate } from '../../utils/dates';
import { ROK_KARTICA, ROK_VOZILO } from './rokovi';
import POSILJALAC from '../../config/posiljalac';
import { ucitajDocx } from '../../utils/docx';

/* Word (.docx) opomena ovlašćenom licu firme — istek roka za preuzimanje podataka sa kartica vozača i tahografa.
   Jedan dokument, svaka firma na svojoj strani. Biblioteka `docx` se učitava tek kad zatreba.
   Pravni osnov u tekstu je iz Zakona (docs/zakon_...): čl. 15 st. 5 (obaveza), čl. 57 t. 24 i čl. 55 t. 40 (kazne). */

const FONT = 'Calibri';
const ZELENA = '237A4B';
const SIVA = '5F6F69';

export async function generisiOpomene(firme, { posiljalac = POSILJALAC, datum = new Date() } = {}) {
    const d = await ucitajDocx();
    const {
        Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell, WidthType, AlignmentType, BorderStyle,
        ShadingType, PageBreak, HeadingLevel,
    } = d;

    const p = (text, opts = {}) => new Paragraph({
        spacing: { after: opts.after ?? 120, line: 276 },
        alignment: opts.align || AlignmentType.JUSTIFIED,
        children: (Array.isArray(text) ? text : [text]).map((t) => (typeof t === 'string' ? new TextRun({ text: t, font: FONT, size: 22 }) : t)),
    });
    const run = (text, o = {}) => new TextRun({ text, font: FONT, size: o.size || 22, bold: o.bold, color: o.color, italics: o.italics });
    const tanka = { style: BorderStyle.SINGLE, size: 4, color: 'CBD3CF' };
    const celija = (text, o = {}) => new TableCell({
        width: o.width ? { size: o.width, type: WidthType.PERCENTAGE } : undefined,
        shading: o.header ? { type: ShadingType.CLEAR, color: 'auto', fill: 'EFF3F1' } : (o.fill ? { type: ShadingType.CLEAR, color: 'auto', fill: o.fill } : undefined),
        margins: { top: 60, bottom: 60, left: 100, right: 100 },
        borders: { top: tanka, bottom: tanka, left: tanka, right: tanka },
        children: [new Paragraph({ children: [run(String(text ?? ''), { bold: o.header || o.bold, size: 20, color: o.color })] })],
    });
    const tabela = (kolone, redovi) => new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        rows: [
            new TableRow({ tableHeader: true, children: kolone.map((k) => celija(k.naslov, { header: true, width: k.sirina })) }),
            ...redovi.map((r) => new TableRow({ children: kolone.map((k) => celija(k.vrednost(r), { width: k.sirina, color: k.boja ? k.boja(r) : undefined, bold: k.bold })) })),
        ],
    });
    const bojaStatusa = (r) => (r.st.kind === 'EXPIRED' || r.st.kind === 'NEVER' ? 'C62F37' : r.st.kind === 'WARN' ? '95600B' : undefined);
    const poslednje = (r) => (r.last ? fmtDate(r.last) : r.approx ? `${fmtDate(r.approx)}*` : '—');

    const sekcije = firme.map((f, i) => {
        const c = f.firma;
        const deca = [];
        if (i > 0) deca.push(new Paragraph({ children: [new PageBreak()] }));
        deca.push(
            /* zaglavlje pošiljaoca */
            new Paragraph({ spacing: { after: 20 }, children: [run(posiljalac.naziv.toUpperCase(), { bold: true, size: 28, color: ZELENA })] }),
            new Paragraph({ spacing: { after: 20 }, children: [run(posiljalac.punNaziv, { size: 16, color: SIVA })] }),
            new Paragraph({ spacing: { after: 360 }, border: { bottom: { style: BorderStyle.SINGLE, size: 8, color: ZELENA, space: 6 } },
                children: [run(`${posiljalac.adresa}, ${posiljalac.mesto}  ·  PIB ${posiljalac.pib}  ·  MB ${posiljalac.maticniBroj}`, { size: 16, color: SIVA })] }),
            p([run(c.name || '', { bold: true })], { after: 0, align: AlignmentType.LEFT }),
        );
        [c.address, [c.city, c.country].filter(Boolean).join(', '), c.tin ? `PIB: ${c.tin}` : null]
            .filter(Boolean).forEach((l) => deca.push(p(l, { after: 0, align: AlignmentType.LEFT })));
        if (c.mainPerson) deca.push(p([run('n/r: '), run(c.mainPerson, { bold: true }), run(c.mainPersonPosition ? `, ${c.mainPersonPosition}` : '')], { after: 0, align: AlignmentType.LEFT }));
        deca.push(
            p(`${posiljalac.grad}, ${fmtDate(datum)}`, { after: 360, align: AlignmentType.RIGHT }),
            new Paragraph({ heading: HeadingLevel.HEADING_1, alignment: AlignmentType.CENTER, spacing: { after: 120 },
                children: [run('OPOMENA', { bold: true, size: 32, color: ZELENA })] }),
            p([run('Predmet: ', { bold: true }), run('istek roka za preuzimanje (očitavanje) podataka sa kartica vozača i digitalnih tahografa')], { after: 240 }),
            p('Poštovani,'),
            p('Obaveštavamo Vas da je za vozače i vozila navedene u nastavku istekao ili uskoro ističe rok za preuzimanje podataka sa kartice vozača, odnosno sa digitalnog tahografa ugrađenog u vozilo.'),
            p([
                run('Prevoznik je dužan da obezbedi da se podaci sa digitalnog tahografa i kartice vozača preuzimaju u propisanim rokovima (član 15. stav 5. Zakona o radnom vremenu posade vozila u drumskom prevozu i tahografima, „Službeni glasnik RS“ br. 96/15, 95/18 i 96/25): '),
                run(`podaci sa kartice vozača najkasnije na svakih ${ROK_KARTICA} dana, a podaci sa tahografa vozila najkasnije na svakih ${ROK_VOZILO} dana.`, { bold: true }),
            ], { after: 240 }),
        );
        if (f.vozaci.length) {
            deca.push(p([run(`Kartice vozača (rok ${ROK_KARTICA} dana)`, { bold: true, color: ZELENA })], { after: 80 }));
            deca.push(tabela([
                { naslov: 'Vozač', sirina: 30, vrednost: (r) => r.naziv },
                { naslov: 'Broj kartice', sirina: 20, vrednost: (r) => r.cardNumber || '—' },
                { naslov: 'Poslednje očitavanje', sirina: 17, vrednost: poslednje },
                { naslov: 'Dana od očitavanja', sirina: 13, vrednost: (r) => (r.st.daysSince ?? '—') },
                { naslov: 'Stanje', sirina: 20, vrednost: (r) => r.st.label, boja: bojaStatusa, bold: true },
            ], f.vozaci));
            if (f.vozaci.some((r) => !r.last && r.approx)) {
                deca.push(p([run('* Procena prema poslednjem danu sa aktivnostima — podaci su uvezeni pre uvođenja evidencije očitavanja.', { italics: true, size: 18, color: SIVA })], { after: 120 }));
            }
            deca.push(p('', { after: 120 }));
        }
        if (f.vozila.length) {
            deca.push(p([run(`Tahografi vozila (rok ${ROK_VOZILO} dana)`, { bold: true, color: ZELENA })], { after: 80 }));
            deca.push(tabela([
                { naslov: 'Registracija', sirina: 22, vrednost: (r) => r.naziv },
                { naslov: 'Broj šasije', sirina: 28, vrednost: (r) => r.identificationNumber || '—' },
                { naslov: 'Poslednje očitavanje', sirina: 17, vrednost: poslednje },
                { naslov: 'Dana od očitavanja', sirina: 13, vrednost: (r) => (r.st.daysSince ?? '—') },
                { naslov: 'Stanje', sirina: 20, vrednost: (r) => r.st.label, boja: bojaStatusa, bold: true },
            ], f.vozila));
            deca.push(p('', { after: 120 }));
        }
        deca.push(
            p([
                run('Molimo Vas da preuzimanje podataka izvršite '), run('odmah, a najkasnije do isteka navedenih rokova.', { bold: true }),
                run(' Nepreuzimanje podataka u propisanom roku je prekršaj za koji je za pravno lice propisana novčana kazna od 30.000 dinara, odnosno 120.000 dinara ako usled toga dođe do gubitka podataka ili nemogućnosti njihovog čitanja (član 57. stav 1. tačka 24) i član 55. stav 1. tačka 40) Zakona), uz kaznu za odgovorno lice u pravnom licu.'),
            ]),
            p('Za sva pitanja i dogovor o terminu očitavanja stojimo Vam na raspolaganju.', { after: 480 }),
            p('S poštovanjem,', { after: 600, align: AlignmentType.LEFT }),
            p('______________________________', { after: 0, align: AlignmentType.LEFT }),
            p([run(posiljalac.naziv, { bold: true })], { after: 0, align: AlignmentType.LEFT }),
            p([run(posiljalac.potpisnik, { size: 20, color: SIVA })], { align: AlignmentType.LEFT }),
        );
        return deca;
    });

    const doc = new Document({
        creator: posiljalac.naziv,
        title: 'Opomena — rok za očitavanje tahografa',
        styles: { default: { document: { run: { font: FONT, size: 22 } } } },
        sections: [{
            properties: { page: { margin: { top: 1134, bottom: 1134, left: 1247, right: 1134 } } }, // ~2 cm
            children: sekcije.flat(),
        }],
    });
    const blob = await Packer.toBlob(doc);
    const ime = firme.length === 1 ? firme[0].firma.name : `${firme.length} firmi`;
    saveAs(blob, `Opomena - rok za očitavanje - ${ime} - ${fmtDate(datum)}.docx`.replace(/[\\/:*?"<>|]/g, ' '));
}
